import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Calendar, Camera, Clock, MapPin, FileText, Users, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { bookingService } from '../../services/bookingService';
import { BookingCreate, ROOMS } from '../../types/booking';
import SelfieCapture from '../../components/appointments/SelfieCapture';

const AGENDA_MIN = 50;
const AGENDA_MAX = 1000;

export default function BookAppointmentPage() {
  const navigate = useNavigate();

  const [appointmentAt, setAppointmentAt] = useState('');
  const [roomId, setRoomId] = useState('');
  const [agenda, setAgenda] = useState('');
  const [selfieB64, setSelfieB64] = useState<string | null>(null);
  const [guestExpanded, setGuestExpanded] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Min datetime = now + 1 minute (ISO local format for <input type="datetime-local">)
  const minDatetime = new Date(Date.now() + 60_000).toISOString().slice(0, 16);

  const agendaLen = agenda.length;
  const agendaValid = agendaLen >= AGENDA_MIN && agendaLen <= AGENDA_MAX;

  const { mutate, isPending, error: mutationError } = useMutation({
    mutationFn: (data: BookingCreate) => bookingService.create(data),
    onSuccess: (booking) => {
      navigate(`/trading/appointments/confirmation/${booking.id}`, {
        state: { booking, selfie_b64: selfieB64 },
      });
    },
  });

  const errorMessage = formError ?? (mutationError instanceof Error ? mutationError.message : null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!roomId) {
      setFormError('Please select a room.');
      return;
    }
    if (!agendaValid) {
      setFormError(`Agenda must be ${AGENDA_MIN}–${AGENDA_MAX} characters (currently ${agendaLen}).`);
      return;
    }
    if (guestExpanded && !guestName.trim()) {
      setFormError('Guest name is required when adding a guest.');
      return;
    }

    mutate({
      appointment_at: new Date(appointmentAt).toISOString(),
      room_id: roomId,
      agenda,
      guest_name: guestExpanded && guestName ? guestName : undefined,
      guest_email: guestExpanded && guestEmail ? guestEmail : undefined,
      selfie_b64: selfieB64 ?? undefined,
    });
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Calendar className="h-7 w-7 text-blue-500" />
          Book an Appointment
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Schedule a branch visit with your AI Banker to discuss your investment needs.
        </p>
      </div>

      {errorMessage && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400 text-sm">
          {errorMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 space-y-5">
        {/* Date & Time */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            <Clock className="inline h-4 w-4 mr-1" />
            Date & Time
          </label>
          <input
            type="datetime-local"
            value={appointmentAt}
            min={minDatetime}
            onChange={(e) => setAppointmentAt(e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>

        {/* Room Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            <MapPin className="inline h-4 w-4 mr-1" />
            Room
          </label>
          <select
            value={roomId}
            onChange={(e) => setRoomId(e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="">Select a room…</option>
            {ROOMS.map((room) => (
              <option key={room.id} value={room.id}>
                {room.name}
              </option>
            ))}
          </select>
        </div>

        {/* Agenda */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            <FileText className="inline h-4 w-4 mr-1" />
            Agenda
          </label>
          <textarea
            value={agenda}
            onChange={(e) => setAgenda(e.target.value)}
            rows={5}
            placeholder={`Describe what you'd like to discuss (${AGENDA_MIN}–${AGENDA_MAX} characters)…`}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 placeholder-gray-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
          />
          <p
            className={`text-xs mt-1 ${
              agendaLen < AGENDA_MIN
                ? 'text-amber-500'
                : agendaLen > AGENDA_MAX
                ? 'text-red-500'
                : 'text-green-600 dark:text-green-400'
            }`}
          >
            {agendaLen} / {AGENDA_MAX} characters{agendaLen < AGENDA_MIN ? ` (minimum ${AGENDA_MIN})` : ''}
          </p>
        </div>

        {/* Selfie Capture */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <Camera className="inline h-4 w-4 mr-1" />
            Your Photo <span className="text-gray-400 font-normal">(optional — for your access card)</span>
          </label>
          <SelfieCapture onCapture={setSelfieB64} />
        </div>

        {/* Collapsible Guest Section */}
        <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setGuestExpanded((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          >
            <span className="flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-400" />
              Add a spouse or guest +
            </span>
            {guestExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {guestExpanded && (
            <div className="px-4 pb-4 pt-1 grid grid-cols-2 gap-4 border-t border-gray-200 dark:border-gray-600">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Guest Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Full name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Guest Email <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            disabled={isPending}
            className="flex-1 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
          >
            {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />}
            {isPending ? 'Booking…' : 'Confirm Booking'}
          </button>
        </div>
      </form>
    </div>
  );
}
